import { createTheme, darken } from '@mui/material/styles';
import { grey } from '@mui/material/colors';

import { typography } from './typography';
import { breakpoints } from './breakpoints';
import { palette } from './colors';

const getTheme = () =>
  createTheme({
    palette: palette(),
    typography,
    breakpoints,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            textRendering: 'optimizeLegibility',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 4,
            borderRadius: '4px 4px 0 0',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.body1,
            minWidth: 0,
            textTransform: 'capitalize',
            letterSpacing: 0,
          }),
          textColorSecondary: ({ theme }) => ({
            color: theme.palette.secondary.main,
            opacity: 0.7,
            '&.Mui-selected': {
              color: theme.palette.secondary.main,
              opacity: 1,
            },
            '&:disabled': {
              color: theme.palette.secondary.main,
              opacity: 0.3,
            },
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            lineHeight: 1.43,
            '&:first-of-type': {
              paddingLeft: theme.spacing(3),
            },
            '&:last-of-type': {
              paddingRight: theme.spacing(3),
            },
          }),
          head: {
            lineHeight: '2rem',
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: ({ theme }) => ({
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
          }),
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          rounded: {
            borderRadius: 8,
          },
        },
      },
      //TODO: use dense instead
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.body2,
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 48,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            [`&.MuiDialog-paper`]: {
              [theme.breakpoints.down('sm')]: {
                maxWidth: `calc(100% - ${theme.spacing(2)})`,
                margin: theme.spacing(1),
              },
            },
            [`&.MuiDialog-paperFullWidth`]: {
              width: `calc(100% - ${theme.spacing(2)})`,
            },
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2, 3),
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            maxWidth: '100%',
          },
          label: {
            display: 'inline-block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      },
      // TODO: do we need it?
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: grey[700],
          },
          sizeSmall: ({ theme }) => ({
            fontSize: '1.6rem',
            padding: theme.spacing(0.5),
          }),
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          size: 'large',
        },
        styleOverrides: {
          containedSecondary: ({ theme }) => ({
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.primary.main,
            transition: `opacity ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeOut}`,

            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
              opacity: 0.7,
            },
            '&.Mui-disabled': {
              backgroundColor: darken(theme.palette.secondary.main, 0.18),
              color: darken(theme.palette.secondary.dark, 0.16),
            },
          }),
          containedPrimary: ({ theme }) => ({
            '&.Mui-disabled': {
              backgroundColor: darken(theme.palette.secondary.main, 0.18),
              color: darken(theme.palette.secondary.dark, 0.16),
            },
          }),
        },
      },
      MuiLink: {
        defaultProps: {
          variant: 'body1',
          underline: 'hover',
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 2,
        },
        styleOverrides: {
          root: {
            marginBottom: 0,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(3),
            '& + .MuiCardContent-root': {
              paddingTop: 0,
            },
          }),
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(3),
          }),
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(3),
            justifyContent: 'flex-end',
          }),
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiTableSortLabel-icon': {
              background: theme.palette.primary.main,
              borderRadius: '50%',
              width: 24,
              height: 24,
              padding: theme.spacing(0.5),
              color: theme.palette.common.white,
            },
            '&:hover:not(.Mui-active)': {
              '& .MuiTableSortLabel-icon': {
                transform: 'none', // Makes default sorting order DESC
                transition: 'none',
              },
            },
            '&.Mui-active .MuiTableSortLabel-icon': {
              color: theme.palette.common.white,
            },
          }),
        },
      },
    },
  });

export default getTheme;
